import React, { useEffect, useRef, useState } from 'react';

// Degrees. A finger gets more than a mouse does, for two reasons: it is
// resting on the card so the hand covers part of what it is turning, and a
// phone holds the card at about a third the physical size, where the same
// angle reads as a smaller movement.
const MAX_TILT = 9;
const MAX_TILT_TOUCH = 16;
const DRAG_SLOP = 6; // px a finger may wander before a tap counts as a drag

/**
 * A business card that is also the contact block: everything on it is a link.
 *
 * Both faces exist at all times, English one side and Japanese the other, the
 * way a real bilingual meishi is printed. The site language decides which one
 * starts facing you, and clicking the card turns it over — that only turns the
 * card, it does not touch the page language.
 *
 * The card is not itself a button: a click handler that ignores clicks landing
 * on a link keeps the surface made of links, which is what broke the last time
 * the whole face was one button.
 *
 * Layout follows the Pierce & Pierce cards: a detail top left, the firm top
 * right, name and title centred, contact details across the foot, with the
 * surname in full caps as "Patrick BATEMAN" is. The type is DEBOSSED — pressed
 * into pale stock — and the pointer drives both the tilt and the direction of
 * the light, so the pressed letters shade from the side the light is on.
 */
function Face({ ja, email, socials, back, hidden }) {
  return (
    // `is-away` is the side currently turned away from you. It carries the
    // hiding as well as the tabIndex, because backface-visibility could not be
    // trusted to do it — see the note on .meishi-face in App.css.
    <div className={`meishi-face${back ? ' is-back' : ''}${hidden ? ' is-away' : ''}`}>
      <div className="meishi-plate">
        <a className="m-tl" href={`mailto:${email}`} tabIndex={hidden ? -1 : 0}>
          {email}
        </a>

        <span className="m-tr">
          <span className="m-firm">Amazon</span>
        </span>

        <span className="m-mid">
          <span className={`m-name${ja ? ' jp' : ''}`}>
            {ja ? (
              '廣津 和哉'
            ) : (
              <>
                Kazuya <em>Hirotsu</em>
              </>
            )}
          </span>
          <span className={`m-title${ja ? ' jp' : ''}`}>
            {ja ? 'ソフトウェアエンジニア' : 'Software Engineer'}
          </span>
        </span>

        <span className="m-foot">
          {socials.map((s) => (
            <a key={s.url} href={s.url} target="_blank" rel="noreferrer" tabIndex={hidden ? -1 : 0}>
              {s.label}
            </a>
          ))}
        </span>
      </div>

      <span className="meishi-glare" aria-hidden="true" />
    </div>
  );
}

export default function Meishi({ email, socials = [], language = 'en' }) {
  const wrapRef = useRef(null);
  const cardRef = useRef(null);
  const touch = useRef({ down: false, moved: false, sx: 0, sy: 0 });
  const [flipped, setFlipped] = useState(false);
  // These have to be state, not classList: className is rendered from state, so
  // a re-render would wipe any class added imperatively — which is exactly what
  // made the card fly back up and vanish on the first click.
  const [dealt, setDealt] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setDealt(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setDealt(true);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Where the pointer is, as a fraction of the card, driving the tilt, the
  // glare and the direction the light presses the type from. The light tracks
  // the tilt rather than being fixed, so the deboss deepens as the card turns
  // further — the whole effect scales together instead of the angle running
  // ahead of the shading.
  const tiltTo = (clientX, clientY, max = MAX_TILT) => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (clientX - r.left) / r.width;
    const py = (clientY - r.top) / r.height;
    const lightScale = 1.6 * (max / MAX_TILT);
    el.style.setProperty('--ry', `${(px - 0.5) * 2 * max}deg`);
    el.style.setProperty('--rx', `${(0.5 - py) * 2 * max}deg`);
    el.style.setProperty('--mx', `${px * 100}%`);
    el.style.setProperty('--my', `${py * 100}%`);
    el.style.setProperty('--lx', `${(px - 0.5) * lightScale}px`);
    el.style.setProperty('--ly', `${(py - 0.5) * lightScale}px`);
  };

  // A mouse tilts the card by hovering it. A finger has to be holding it —
  // there is no hover to read — so touch tilts only while pressed, and the
  // card levels out again when you let go.
  const onPointerDown = (e) => {
    if (e.pointerType !== 'touch') return;
    touch.current = { down: true, moved: false, sx: e.clientX, sy: e.clientY };
    // Keep the events coming even when the finger slides off the card, so a
    // big gesture doesn't leave the tilt stuck at whatever the edge was.
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (err) {
      /* capture is a nicety; the tilt still works without it */
    }
    tiltTo(e.clientX, e.clientY, MAX_TILT_TOUCH);
  };

  const onPointerMove = (e) => {
    if (e.pointerType === 'touch') {
      if (!touch.current.down) return;
      if (
        !touch.current.moved &&
        Math.hypot(e.clientX - touch.current.sx, e.clientY - touch.current.sy) > DRAG_SLOP
      ) {
        touch.current.moved = true;
      }
    }
    tiltTo(e.clientX, e.clientY, e.pointerType === 'touch' ? MAX_TILT_TOUCH : MAX_TILT);
  };

  const onPointerUp = (e) => {
    if (e.pointerType !== 'touch') return;
    touch.current.down = false;
    level();
  };

  const level = () => {
    const el = wrapRef.current;
    if (!el) return;
    el.style.setProperty('--ry', '0deg');
    el.style.setProperty('--rx', '0deg');
    el.style.setProperty('--mx', '50%');
    el.style.setProperty('--my', '50%');
    el.style.setProperty('--lx', '-0.6px');
    el.style.setProperty('--ly', '-0.7px');
  };

  const siteJa = language === 'ja';
  const frontJa = flipped ? !siteJa : siteJa;   // the face you're looking at
  const otherLabel = frontJa ? 'English' : '日本語';

  return (
    <div className="meishi-wrap">
      <div
        className={[
          'meishi',
          dealt && 'is-dealt',
          flipped && 'is-flipped',
        ]
          .filter(Boolean)
          .join(' ')}
        ref={wrapRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => {
          touch.current.down = false;
          level();
        }}
        onPointerLeave={level}
        onClick={(e) => {
          // the card turns when you click it, but not when you click a link on it
          if (e.target.closest('a')) return;
          // ...and not when the "click" was really a finger dragging it around
          if (touch.current.moved) {
            touch.current.moved = false;
            return;
          }
          setFlipped((v) => !v);
        }}
      >
        {/* One transform per layer, each with its own duration. Flip and tilt
            used to share `transform` on one element, so whichever transition
            was active won — and since you are always hovering when you click,
            the flip kept inheriting the fast tilt timing. */}
        <div className="meishi-drop" ref={cardRef}>
          <div className="meishi-tilt">
            <div className="meishi-card">
              <Face ja={siteJa} email={email} socials={socials} hidden={flipped} />
              <Face ja={!siteJa} email={email} socials={socials} back hidden={!flipped} />
            </div>
          </div>
        </div>
      </div>

      {/* Mouse users click the card itself. This exists so the card can still be
          turned from the keyboard; it stays out of the way until focused. */}
      <button
        type="button"
        className="meishi-turn"
        aria-pressed={flipped}
        onClick={() => setFlipped((v) => !v)}
      >
        {`Turn the card — ${otherLabel}`}
      </button>
    </div>
  );
}
